import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { ElementType, PropOrElemType } from 'src/app/_objects/element-type';
import { PropertyPanelDef } from 'src/app/_objects/interfaces/property-panel';
import { ReportProperties } from 'src/app/_objects/report-properties';
import {
  Notifier,
  PropertyPreviewMetadata,
  ReportTemplateDesignService,
  UpdateMetadata,
} from 'src/app/_services/report-template-design.service';

@Component({
    selector: 'app-property-preview',
    templateUrl: './property-preview.component.html',
    styleUrls: ['./property-preview.component.scss'],
    standalone: false
})
export class PropertyPreviewComponent implements OnDestroy {
  public propertyTableColumns = ['property'];
  public commonPropPanels: PropertyPanelDef[];
  public selectedPropPanels: PropertyPanelDef[];

  private mySubscriber: Subscription[];
  private notifier: Notifier;
  @Output() public closePropertyPanel = new EventEmitter<any>();
  @Output() public closeSnackBar = new EventEmitter<any>();

  public permissionForActions: { [actionName: string]: boolean };

  constructor(private designService: ReportTemplateDesignService) {
    this.mySubscriber = [];
    this.notifier = {
      componentName: 'property-preview.component',
      ids: { pageId: '', elementId: '' },
    };

    this.permissionForActions = {
      save: true,
    };

    this.mySubscriber.push(
      this.designService.onReady.subscribe((message: string) => {
        this.viewCommonElement();
        this.viewSelectedElement();
      })
    );

    this.mySubscriber.push(
      this.designService.onEndUpdate.subscribe((message: string) => {
        this.viewCommonElement();
      })
    );

    this.mySubscriber.push(
      this.designService.onSelectedElement.subscribe((message: string) => {
        this.viewSelectedElement();
      })
    );
  }

  public ngOnDestroy() {
    this.mySubscriber.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  public closePanel(): void {
    this.closePropertyPanel.emit();
  }

  public viewSelectedElement() {
    const selectedElemProps: ReportProperties[] = this.designService.getSelectedElementProperties();
    const application = this.getApplication();
    this.selectedPropPanels = [];
    selectedElemProps.forEach((reportProperties: ReportProperties) => {
      if (this.getPanelType(reportProperties.getPropOrElemType()) === 'Page') {
        this.selectedPropPanels.push({
          propId: reportProperties.getId(),
          title: reportProperties.getTitle(),
          propOrElemType: reportProperties.getPropOrElemType(),
          mapTableData: reportProperties.getMapProperties(),
        });
      }

      if (this.getPanelType(reportProperties.getPropOrElemType()) !== 'Page') {
        //if (application === 'ViSOL') {
        //  this.selectedPropPanels.push({
        //    propId: reportProperties.getId(),
        //    title: reportProperties.getTitle(),
        //    propOrElemType: reportProperties.getPropOrElemType(),
        //    mapTableData: reportProperties.getMapProperties(),
        //  });
        //} else if (application === 'IPM') {
        //  this.selectedPropPanels.push({
        //    propId: reportProperties.getId(),
        //    title: reportProperties.getTitle(),
        //    propOrElemType: reportProperties.getPropOrElemType(),
        //    mapTableData: reportProperties.getMapProperties(),
        //  });
        //} else {
        //  this.selectedPropPanels.push({
        //    propId: reportProperties.getId(),
        //    title: reportProperties.getTitle(),
        //    propOrElemType: reportProperties.getPropOrElemType(),
        //    mapTableData: reportProperties.getMapProperties(),
        //  });
        //}
        this.selectedPropPanels.push({
          propId: reportProperties.getId(),
          title: reportProperties.getTitle(),
          propOrElemType: reportProperties.getPropOrElemType(),
          mapTableData: reportProperties.getMapProperties(),
        });
      }
    });
    this.selectedPropPanels =
      this.selectedPropPanels.length > 1
        ? this.selectedPropPanels.filter(x => x.propOrElemType !== ElementType.Page)
        : this.selectedPropPanels;
  }
  public viewCommonElement() {
    const commProps: ReportProperties[] = this.designService.getCommonProperties();
    this.commonPropPanels = [];
    commProps.forEach((reportProperties: ReportProperties) => {
      this.commonPropPanels.push({
        propId: reportProperties.getId(),
        title: reportProperties.getTitle(),
        propOrElemType: reportProperties.getPropOrElemType(),
        mapTableData: reportProperties.getMapProperties(),
      });
    });
  }
  public onCommPropChange(data: UpdateMetadata) {
    this.designService.notifyUpdateCommonProperties(this.notifier, data);
  }

  public onElemPropChange(data: UpdateMetadata) {
    if (data.oldValue !== data.value) {
      const template = { ...this.designService.originTemplate };
      if (!(data.name === 'columns' && data.parentName === 'table')) {
        template.reportTemplateContent = JSON.stringify(this.designService.getTemplate());
      }
      this.designService.templateForUndo.push(template);
      this.designService.notifyUpdateElementProperties(this.notifier, data);
      if (data.name === 'hasHeader' || data.name === 'hasFooter') {
        this.designService.reloadReportTemplate(this.designService.getTempTemplate(), true);
      }
    }
  }
  public getPanelType(propOrElemType: PropOrElemType): string {
    const types: ElementType[] = [
      ElementType.Page,
      ElementType.Text,
      ElementType.Line,
      ElementType.Image,
      ElementType.Rectangle,
      ElementType.Summary,
      ElementType.SubTotal,
      ElementType.Picture,
      ElementType.DataPlate,
      ElementType.MeasurementPoint,
      ElementType.Signature,
      ElementType.VisualEvents,
      ElementType.Table,
      ElementType.DropDownList,
      ElementType.InteractiveIso,
      ElementType.Section,
    ];
    for (const type of types) {      
      if (propOrElemType.indexOf(type) === 0) {
        return type;
      }
    }
    return null;
  }
  public isVisolTable(propOrElemType: PropOrElemType): boolean {
    const type = this.getPanelType(propOrElemType);
    return type === ElementType.Summary || type === ElementType.SubTotal || type === ElementType.Picture || type === ElementType.DataPlate;
  }
  public isGeneral(propOrElemType: PropOrElemType): boolean {
    const type = this.getPanelType(propOrElemType);
    return type !== ElementType.SubTotal && type !== ElementType.DataPlate;
  }
  public isTitle(propOrElemType: PropOrElemType): boolean {
    const type = this.getPanelType(propOrElemType);
    return type === ElementType.Summary || type === ElementType.SubTotal || type === ElementType.Picture || type === ElementType.DataPlate;
  }
  public isTable(propOrElemType: PropOrElemType): boolean {
    const type = this.getPanelType(propOrElemType);
    return type === ElementType.Summary || type === ElementType.SubTotal || type === ElementType.Picture;

  }
  public isDataPlate(propOrElemType: PropOrElemType): boolean {
    const type = this.getPanelType(propOrElemType);
    return type === ElementType.DataPlate;
  }
  public activeTab(propOrElemType: PropOrElemType, selected: string): string {
    const type = this.getPanelType(propOrElemType);
    switch (selected) {
      case 'Table':
        if (type === ElementType.DataPlate) {
          return 'Title';
        } else {
          return selected;
        }
        return selected;
      case 'ImageInfo':
        if (type === ElementType.DataPlate) {
          return selected;
        } else if (type === ElementType.SubTotal) {
          return 'Title';
        } else {
          return 'General';
        }
      case 'General':
      case 'Title':
      default:
        if (type === ElementType.DataPlate || type === ElementType.SubTotal) {
          return 'Title';
        } else {
          return selected ? selected : 'General';
        }
    }
  }

  public getType(propertyPanelDef: PropertyPanelDef, category: string): string {
    let type: string = null;
    if (!propertyPanelDef.mapTableData)
      return type;

    propertyPanelDef.mapTableData.forEach(previewDatas => {
      previewDatas.forEach(metadata => {
        if (this.getBottomMetadataName(metadata, category, 'type')) {
          type = metadata.value as string;
          return;
        }
      });
    });
    return type;
  }
  public getInputType(propertyPanelDef: PropertyPanelDef, category: string): string {
    let inputType: string = null;
    if (!propertyPanelDef.mapTableData)
      return inputType;

    propertyPanelDef.mapTableData.forEach(previewDatas => {
      previewDatas.forEach(metadata => {
        if (this.getBottomMetadataName(metadata, category, 'inputType')) {
          inputType = metadata.value as string;
          return;
        }
      });
    });
    return inputType;
  }
  public getDynamicId(propertyPanelDef: PropertyPanelDef, category: string): string {
    let dynamicId: string = null;
    if (!propertyPanelDef.mapTableData)
      return dynamicId;

    propertyPanelDef.mapTableData.forEach(previewDatas => {
      previewDatas.forEach(metadata => {
        if (this.getBottomMetadataName(metadata, category, 'dynamicId')) {
          dynamicId = metadata.value as string;
          return;
        }
      });
    });
    return dynamicId;
  }
  public getMaxLength(propertyPanelDef: PropertyPanelDef, category: string): number {
    let maxLength: number = null;
    if (!propertyPanelDef.mapTableData)
      return maxLength;

    propertyPanelDef.mapTableData.forEach(previewDatas => {
      previewDatas.forEach(metadata => {
        if (category === 'TITLE' || category === 'IMAGEINFOTITLE') {
          if (this.getBottomMetadataName(metadata, category, 'validation.maxLength')) {
            maxLength = metadata.value as number;
            return;
          }
        } else {
          if (this.getBottomMetadataName(metadata, category, 'maxLength')) {
            maxLength = metadata.value as number;
            return;
          }
        }
      })
    });

    return maxLength;
  }
  public isLayoutData(previewData: PropertyPreviewMetadata, panelType: string): boolean {
    const application = this.getApplication();
    if (application === 'ViSOL') {
      return (
        0 > ReportTemplateDesignService.PROP_DYNAMIC_ATTR4VISOL.indexOf(previewData.name) &&
        0 > ReportTemplateDesignService.PROP_DYNAMIC_ATTR4IPM.indexOf(previewData.name)
      );
    } else {
      return (
        0 > ReportTemplateDesignService.PROP_DYNAMIC_ATTR4IPM.indexOf(previewData.name) &&
        0 > ['dynamicId'].indexOf(previewData.name)
      );
    }
  }
  public isDynamicData(previewData: PropertyPreviewMetadata, panelType: string): boolean {
    const application = this.getApplication();
    if (panelType === 'Text') {
      if (application === 'ViSOL') {
        return 0 <= ReportTemplateDesignService.PROP_DYNAMIC_ATTR4VISOL.indexOf(previewData.name);
      } else if (application === 'IPM') {
        return 0 <= ReportTemplateDesignService.PROP_DYNAMIC_ATTR4IPM.indexOf(previewData.name);
      } else {
        return !this.isLayoutData(previewData, panelType) && 'dynamicId' !== previewData.name;
      }
    } else if (application === 'ViSOL') {
      // TODO: Delete when ViSOL Image corresponds to DynamicId
      if (panelType === 'Image' && previewData.name === 'type') {
        previewData.selectItems = previewData.selectItems.filter(_ => _.label === 'static');
      }
      return !this.isLayoutData(previewData, panelType);
    } else {
      return !this.isLayoutData(previewData, panelType);
    }
  }
  public isDisplayExpansionPanel(group: string, seleceTab: string): boolean {
    switch (seleceTab) {
      case 'Layout':
      default:
        return group === 'Position'
          || group === 'Size'
          || group === 'TextBox'
          || group === 'Line'
          || group === 'Background'
          || group === 'Font';
      case 'DataBinding':
        return group === 'Radio'
          || group === 'DataBinding'
          || group === 'Select'
          || group === 'Input';
      case 'Title':
        return group === 'Position'
          || group === 'Size'
          || group === 'TextBox'
          || group === 'Line'
          || group === 'Background'
          || group === 'Font'
          || group === 'Radio'
          || group === 'DataBinding'
          || group === 'Select'
          || group === 'Input';
      case 'Table':
        return group === 'Position'
          || group === 'Size'
          || group === 'TextBox'
          || group === 'Line'
          || group === 'Background'
          || group === 'Font'
          || group === 'DataBinding';
      case 'ImageInfo':
        return group === 'Position'
          || group === 'Size'
      case 'ImageInfoTitle':
        return group === 'TitlePosition'
          || group === 'TitleSize'
          || group === 'TitleTextBox'
          || group === 'TitleLine'
          || group === 'TitleBackground'
          || group === 'TitleFont'
      case 'ImageInfoImage':
        return group === 'Image';
    }
  }
  isDiplayPanel(previewData: PropertyPreviewMetadata[]) {
    return previewData.some(metadata => metadata.isDisplay);
  }
  public isDisplay(propertyPanelDef: PropertyPanelDef): boolean {
    let flag = false;
    if (propertyPanelDef.mapTableData === undefined) {
      return false;
    }
    propertyPanelDef.mapTableData.forEach(previewDatas => {
      previewDatas.forEach(previewData => {
        flag = flag || this.isDynamicData(previewData, propertyPanelDef.propOrElemType);
      });
    });
    return flag;
  }
  public asIsOrder(a, b) {
    return 1;
  }
  public getApplication(): string {
    return this.designService.getTemplateApplication();
  }
  getBottomMetadataName(previewData: PropertyPreviewMetadata, category: string, flag: string) {
    switch (category) {
      case 'IMAGEINFOTITLE':
        return previewData.name === `title.${flag}`;
      case 'IMAGEINFOIMAGE':
        return previewData.name === `image.${flag}`;
      default:
        return previewData.name === flag;
    }
  }
}
